import { Injectable, inject, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, tap } from 'rxjs';
import { TokenService } from './token.service';
import { User, JwtClaims, LoginCredentials, AuthTokens } from '@com.maessa.payroll.frontend/models';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly tokenService = inject(TokenService);
  private readonly router = inject(Router);

  private readonly _currentUser = signal<User | null>(null);
  private readonly _loading = signal(false);

  readonly currentUser = this._currentUser.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly isAuthenticated = computed(() => this._currentUser() !== null);

  private authApiService?: {
    login(creds: LoginCredentials): Observable<AuthTokens>;
    refresh(token: string): Observable<AuthTokens>;
    getProfile(): Observable<User>;
  };

  setAuthApiService(service: {
    login(creds: LoginCredentials): Observable<AuthTokens>;
    refresh(token: string): Observable<AuthTokens>;
    getProfile(): Observable<User>;
  }): void {
    this.authApiService = service;
  }

  login(credentials: LoginCredentials): Observable<AuthTokens> {
    if (!this.authApiService) throw new Error('AuthApiService not configured');
    this._loading.set(true);
    return this.authApiService.login(credentials).pipe(
      tap({
        next: (tokens) => {
          this.tokenService.setToken(tokens.token);
          this.tokenService.setRefreshToken(tokens.refresh_token);
          this._loading.set(false);
        },
        error: () => this._loading.set(false),
      })
    );
  }

  loadProfile(): Observable<User> {
    if (!this.authApiService) throw new Error('AuthApiService not configured');
    return this.authApiService.getProfile().pipe(
      tap((user) => this._currentUser.set(user))
    );
  }

  refreshToken(): Observable<AuthTokens> {
    if (!this.authApiService) throw new Error('AuthApiService not configured');
    const refreshToken = this.tokenService.getRefreshToken();
    if (!refreshToken) throw new Error('No refresh token available');
    return this.authApiService.refresh(refreshToken).pipe(
      tap((tokens) => {
        this.tokenService.setToken(tokens.token);
        this.tokenService.setRefreshToken(tokens.refresh_token);
      })
    );
  }

  logout(): void {
    this.tokenService.clearTokens();
    this._currentUser.set(null);
    this.router.navigate(['/login']);
  }

  hasPermission(permission: string | string[]): boolean {
    const user = this._currentUser();
    if (!user) return false;
    if (user.role === 'administrator' || user.isGlobal) return true;
    if (!user.permissions) return false;

    if (Array.isArray(permission)) {
      return permission.some((perm) =>
        user.permissions.some((p) => p.permission === perm && p.granted)
      );
    }

    return user.permissions.some((p) => p.permission === permission && p.granted);
  }

  hasRole(role: string): boolean {
    const user = this._currentUser();
    if (!user) return false;
    return user.role === role;
  }

  getClaimsFromToken(): JwtClaims | null {
    const token = this.tokenService.getToken();
    if (!token) return null;
    try {
      const payload = token.split('.')[1];
      return JSON.parse(atob(payload)) as JwtClaims;
    } catch {
      return null;
    }
  }

  initFromToken(): Observable<User> | null {
    if (!this.tokenService.hasToken()) return null;
    return this.loadProfile();
  }
}
